import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Camera, Clapperboard, Film, Image as ImageIcon, LayoutGrid, ListChecks, Plus, Smartphone, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CreditBadge } from '../../content-studio/components/CreditBadge';
import { ProductPickerPanel } from '../../content-studio/components/ProductPickerPanel';
import { CsProject } from '../../../../common/models/content-studio.model';
import { PageContainer } from '../../../../common/components/PageContainer';
import { ArdiOrb } from '../../../../components/design-system/common/ArdiOrb';
import { HorizontalScroller } from '../../../../components/design-system/common/HorizontalScroller';

import { StudioBoard, StudioFormat, StudioLook, emptyBoard, isEmptyBoard } from '../studio-contract';
import { useStudioApi } from '../use-studio-api';
import { rememberProducts } from '../use-product-info';
import { Composer, ComposerChip } from './Composer';
import { ProductInfo, productInfoFrom } from './ShotCard';
import { StudioStartState } from './ProductionPage';
import { SELECTED } from '../../../../components/design-system/common/selection';
import { ProductionCard } from './ProductionCard';
import { ConfirmDeleteDialog } from '../../../../components/design-system/common/ConfirmDeleteDialog';
import { DESTRUCTIVE_OUTLINE } from '../../../../components/design-system/admin/styles';
import { FormatMenu, LookMenu } from './BoardSettings';

interface Idea {
    icon: React.ReactNode;
    title: string;
    prompt: string;
    format: StudioFormat;
    look: StudioLook;
    wantsReference?: boolean;
}

function coverOf(p: CsProject & { board?: StudioBoard }): { url?: string; video?: boolean } {
    const shots = p.board?.shots || [];
    for (const s of shots) {
        if (s.still?.url) return { url: s.still.url };
    }
    const gen = (p.canvasGraph || []).find((n: any) => n?.kind === 'generation' && n.asset?.url && n.asset.kind !== 'video');
    // Nothing made yet: the reference it started from stands in.
    return { url: gen?.asset?.url || p.board?.reference?.frames?.[0]?.url || (p.board?.reference?.kind === 'image' ? p.board.reference.url : undefined) };
}

/** How many productions the row shows before "View all". */
const ROW_LIMIT = 12;

/**
 * Studio's front door: one question in the middle of the page — what are we
 * making? — with the box to answer it, products and a reference attachable
 * right there, and ideas for whoever arrives without one.
 *
 * Past productions sit below in a single row that scrolls sideways, so the
 * page stays about the next thing to make; "View all" opens the whole grid.
 */
export const StudioHome: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const api = useStudioApi();
    const [text, setText] = useState('');
    const [format, setFormat] = useState<StudioFormat>('9:16');
    const [look, setLook] = useState<StudioLook>('editorial');
    const [products, setProducts] = useState<ProductInfo[]>([]);
    const [referenceFile, setReferenceFile] = useState<File | null>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    // A picture shows itself in its chip; a video plays a frame of itself.
    const [referenceThumb, setReferenceThumb] = useState<string | null>(null);
    useEffect(() => {
        if (!referenceFile) { setReferenceThumb(null); return undefined; }
        const u = URL.createObjectURL(referenceFile);
        setReferenceThumb(u);
        return () => URL.revokeObjectURL(u);
    }, [referenceFile]);
    const [creating, setCreating] = useState(false);
    const [projects, setProjects] = useState<Array<CsProject & { board?: StudioBoard }>>([]);
    const [loadingProjects, setLoadingProjects] = useState(true);
    const [showAll, setShowAll] = useState(false);
    // Picking several productions to act on them together.
    const [selecting, setSelecting] = useState(false);
    const [picked, setPicked] = useState<Set<string>>(new Set());
    const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
    const toggle = (uuid: string) => {
        setSelecting(true);
        setShowAll(true);
        setPicked((s) => { const n = new Set(s); if (n.has(uuid)) n.delete(uuid); else n.add(uuid); return n; });
    };
    const stopSelecting = () => { setSelecting(false); setPicked(new Set()); };
    useEffect(() => {
        if (!selecting) return undefined;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') stopSelecting(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [selecting]);
    const deletePicked = async () => {
        const uuids = Array.from(picked);
        const results = await Promise.allSettled(uuids.map((u) => api.deleteProject(u)));
        const gone = new Set(uuids.filter((_, i) => results[i].status === 'fulfilled'));
        setProjects((list) => list.filter((x) => !gone.has(x.uuid)));
        // What could not be deleted stays picked, so it can be tried again.
        const left = new Set(uuids.filter((u) => !gone.has(u)));
        setPicked(left);
        if (!left.size) setSelecting(false);
        if (left.size) throw new Error('some productions could not be deleted');
    };
    const [balance, setBalance] = useState<number | null>(null);

    useEffect(() => {
        // Empty productions (opened, then left with nothing in them) are not shown.
        api.listProjects({ limit: 100 })
            .then((r: any) => setProjects((r?.items || []).filter((p: any) => (p.board?.version
                // A production nothing was done in.
                ? !isEmptyBoard(p.board)
                // A canvas project with nothing on it and nothing made in it.
                : (p.canvasGraph || []).length > 0 || (p.assetCount || 0) > 0))))
            .catch(() => undefined)
            .finally(() => setLoadingProjects(false));
        api.getCreditBalance().then((b) => setBalance(b.balance)).catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const ideas: Idea[] = [
        { icon: <Clapperboard />, title: t('STUDIO.IDEAS.COPY_TITLE', 'Recreate a video you love'), prompt: t('STUDIO.IDEAS.COPY_PROMPT', 'Recreate this video with my products, same rhythm, framing and text.'), format: '9:16', look: 'cinematic', wantsReference: true },
        { icon: <Film />, title: t('STUDIO.IDEAS.REEL_TITLE', 'A product reel'), prompt: t('STUDIO.IDEAS.REEL_PROMPT', 'A cinematic 15-second reel of my best product: establishing shot, two material close-ups, someone using it, then the hero shot with our name.'), format: '9:16', look: 'cinematic' },
        { icon: <LayoutGrid />, title: t('STUDIO.IDEAS.WEEK_TITLE', 'A week of posts'), prompt: t('STUDIO.IDEAS.WEEK_PROMPT', 'A week of Instagram posts — one product a day, same style, Monday to Sunday.'), format: '4:5', look: 'editorial' },
        { icon: <ImageIcon />, title: t('STUDIO.IDEAS.LIFESTYLE_TITLE', 'Lifestyle photos'), prompt: t('STUDIO.IDEAS.LIFESTYLE_PROMPT', 'Four lifestyle photos of this product in different rooms that suit it.'), format: '4:5', look: 'editorial' },
        { icon: <Camera />, title: t('STUDIO.IDEAS.DETAIL_TITLE', 'Close-ups and details'), prompt: t('STUDIO.IDEAS.DETAIL_PROMPT', 'Macro and detail shots of the material, with raking light.'), format: '4:5', look: 'cinematic' },
        { icon: <Smartphone />, title: t('STUDIO.IDEAS.UGC_TITLE', 'Phone-style video'), prompt: t('STUDIO.IDEAS.UGC_PROMPT', 'A TikTok-style video that looks filmed on a phone at home: someone discovering the product, natural light, handheld.'), format: '9:16', look: 'ugc' },
    ];
    const chosenIdea = ideas.find((i) => i.prompt === text);

    const create = async (message: string, overrides?: { format?: StudioFormat; look?: StudioLook; blank?: boolean }) => {
        if (creating) return;
        setCreating(true);
        try {
            const name = (message || t('STUDIO.UNTITLED', 'New production')).replace(/\s+/g, ' ').slice(0, 48);
            const project: any = await api.createProject({ name } as any);
            const board: StudioBoard = { ...emptyBoard(), format: overrides?.format || format, look: overrides?.look || look, brief: message || undefined };
            await api.updateProject(project.uuid, { board } as any);
            rememberProducts(products);
            const start: StudioStartState | undefined = overrides?.blank ? undefined : { message, products, referenceFile: referenceFile || undefined };
            navigate(`/marketing/content-studio/${project.uuid}`, { state: start ? { start } : undefined });
        } catch {
            setCreating(false);
        }
    };

    const chips: ComposerChip[] = [
        ...products.map((p) => ({
            key: `p:${p.uuid}`, kind: 'product' as const, label: p.name, thumbnailUrl: p.thumbnailUrl,
            preview: p.imageUrl || p.thumbnailUrl ? { url: (p.imageUrl || p.thumbnailUrl)!, kind: 'image' as const, title: p.name } : undefined,
        })),
        ...(referenceFile ? [{
            key: 'ref', kind: 'reference' as const, label: referenceFile.name,
            thumbnailUrl: (referenceFile.type.startsWith('image/') && referenceThumb) || undefined,
            preview: referenceThumb ? { url: referenceThumb, kind: referenceFile.type.startsWith('video/') ? 'video' as const : 'image' as const, title: referenceFile.name } : undefined,
        }] : []),
    ];

    const card = (p: CsProject & { board?: StudioBoard }, className?: string) => {
        const cover = coverOf(p);
        // Anything made before the board is a canvas project, and opens there.
        const isCanvas = !p.board?.version;
        const shots = p.board?.shots?.length || 0;
        return (
            <ProductionCard
                key={p.uuid}
                className={className}
                name={p.name}
                coverUrl={cover.url}
                subtitle={isCanvas ? t('STUDIO.HOME.CANVAS', 'Canvas project')
                    : shots === 1 ? t('STUDIO.HOME.ONE_SHOT', '1 shot')
                    : shots ? t('STUDIO.HOME.SHOTS', '{{n}} shots', { n: shots })
                    : t('STUDIO.HOME.NO_SHOTS', 'No shots yet')}
                updatedAt={(p as any).updatedAt}
                onOpen={() => navigate(isCanvas ? `/marketing/content-studio/${p.uuid}/canvas` : `/marketing/content-studio/${p.uuid}`)}
                onRename={async (name) => {
                    setProjects((list) => list.map((x) => (x.uuid === p.uuid ? { ...x, name } : x)));
                    await api.updateProject(p.uuid, { name }).catch(() => {
                        setProjects((list) => list.map((x) => (x.uuid === p.uuid ? { ...x, name: p.name } : x)));
                    });
                }}
                onDelete={async () => {
                    await api.deleteProject(p.uuid);
                    setProjects((list) => list.filter((x) => x.uuid !== p.uuid));
                }}
                selected={picked.has(p.uuid)}
                selecting={selecting}
                onToggleSelect={() => toggle(p.uuid)}
            />
        );
    };

    return (
        <PageContainer
            breadcrumbs={[
                { label: t('SIDEBAR.MARKETING.TITLE', 'Marketing') },
                { label: t('SIDEBAR.MARKETING.CONTENT_STUDIO', 'Content Studio') },
            ]}
            status={<CreditBadge balance={balance} compact />}
        >
            {/* ── the question, in the middle ───────────────── */}
            <section className="mx-auto flex min-h-[min(68vh,640px)] w-full max-w-3xl flex-col items-center justify-center pb-10 pt-6">
                <ArdiOrb size={36} className="mb-4" />
                <h1 className="text-balance text-center text-[26px] font-medium tracking-tight text-foreground sm:text-[30px]">
                    {t('STUDIO.HOME.QUESTION', 'What are we making today?')}
                </h1>
                <p className="mt-2 max-w-xl text-balance text-center text-sm text-muted-foreground">
                    {t('STUDIO.HOME.SUBTITLE', 'Photos and videos of your products that look like a real shoot. Tell Ardi what you need.')}
                </p>

                <div className="mt-7 w-full">
                    <Composer
                        size="lg"
                        autoFocus
                        value={text}
                        onChange={setText}
                        onSubmit={() => create(text.trim())}
                        busy={creating}
                        placeholder={t('STUDIO.HOME.PLACEHOLDER', 'What do you want to make? A reel for a new rug, a week of posts, photos for the website…')}
                        chips={chips}
                        onRemoveChip={(key) => (key === 'ref' ? setReferenceFile(null) : setProducts((ps) => ps.filter((p) => `p:${p.uuid}` !== key)))}
                        onAttachReference={(file) => {
                            setReferenceFile(file);
                            // The suggestion names what was dropped: a pin or photo is a picture, not a video.
                            if (!text.trim()) {
                                setText(file.type.startsWith('video/')
                                    ? t('STUDIO.IDEAS.COPY_PROMPT', 'Recreate this video with my products, same rhythm, framing and text.')
                                    : t('STUDIO.IDEAS.COPY_PICTURE_PROMPT', 'Recreate this picture with my products, same framing, light and text.'));
                            }
                        }}
                        onPickProducts={() => setPickerOpen(true)}
                        onMentionProduct={(p) => setProducts((ps) => (ps.some((x) => x.uuid === p.uuid) ? ps : [...ps, p]))}
                        footer={(
                            <div className="ms-auto hidden items-center gap-1.5 sm:flex">
                                <FormatMenu value={format} onChange={setFormat} compact />
                                <LookMenu value={look} onChange={setLook} />
                            </div>
                        )}
                    />
                    {/* On a phone the format and look sit under the box, where there is room. */}
                    <div className="mt-2 flex items-center gap-1.5 sm:hidden">
                        <FormatMenu value={format} onChange={setFormat} compact />
                        <LookMenu value={look} onChange={setLook} />
                    </div>
                </div>

                <div className="mt-5 flex flex-wrap justify-center gap-2">
                    {ideas.map((idea) => (
                        <button
                            key={idea.title}
                            type="button"
                            onClick={() => {
                                setText(idea.prompt);
                                setFormat(idea.format);
                                setLook(idea.look);
                            }}
                            className={cn(
                                'inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full border bg-background px-3 text-[13px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground [&_svg]:size-3.5',
                                chosenIdea === idea && `${SELECTED} text-foreground`,
                            )}
                        >
                            {idea.icon}{idea.title}
                        </button>
                    ))}
                    <button type="button" onClick={() => create('', { blank: true })} disabled={creating}
                        className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full border border-dashed px-3 text-[13px] text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground">
                        <Plus className="size-3.5" />{t('STUDIO.HOME.BLANK_SHORT', 'Empty board')}
                    </button>
                </div>
                <p className={cn('mt-3 h-4 text-center text-xs text-muted-foreground transition-opacity', chosenIdea?.wantsReference && !referenceFile ? 'opacity-100' : 'opacity-0')}>
                    {t('STUDIO.IDEAS.ADD_REFERENCE_DROP', 'Drop the video in the box, or add it with “Reference”.')}
                </p>
            </section>

            {/* ── your productions ───────────────────────────── */}
            <section className="mx-auto w-full max-w-6xl pb-16">
                <div className="mb-3 flex h-8 items-center gap-2">
                    {selecting ? (
                        <>
                            <span className="text-sm font-medium text-foreground">
                                {t('STUDIO.HOME.N_SELECTED', '{{n}} selected', { n: picked.size })}
                            </span>
                            <Button variant="ghost" size="sm" className="text-muted-foreground"
                                onClick={() => setPicked(picked.size === projects.length ? new Set() : new Set(projects.map((p) => p.uuid)))}>
                                {picked.size === projects.length ? t('STUDIO.HOME.SELECT_NONE', 'Select none') : t('STUDIO.HOME.SELECT_ALL', 'Select all')}
                            </Button>
                            <div className="ms-auto flex items-center gap-2">
                                <Button variant="outline" size="sm" className={cn('gap-1.5', DESTRUCTIVE_OUTLINE)} disabled={!picked.size} onClick={() => setConfirmBulkDelete(true)}>
                                    <Trash2 className="size-3.5" />{t('STUDIO.HOME.DELETE', 'Delete')}
                                </Button>
                                <Button variant="ghost" size="sm" onClick={stopSelecting}>{t('COMMON.CANCEL', 'Cancel')}</Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <h2 className="text-sm font-medium text-foreground">{t('STUDIO.HOME.RECENT', 'Your productions')}</h2>
                            {projects.length > 0 && <span className="text-sm text-muted-foreground">{projects.length}</span>}
                            {projects.length > 0 && (
                                <div className="ms-auto flex items-center gap-1">
                                    <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" onClick={() => { setSelecting(true); setShowAll(true); }}>
                                        <ListChecks className="size-3.5" />{t('STUDIO.HOME.SELECT', 'Select')}
                                    </Button>
                                    {projects.length > 4 && (
                                        <Button variant="outline" size="sm" onClick={() => setShowAll((v) => !v)}>
                                            {showAll ? t('STUDIO.HOME.SHOW_LESS', 'Show less') : t('STUDIO.HOME.VIEW_ALL', 'View all')}
                                        </Button>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
                {loadingProjects ? (
                    <div className="flex gap-3 overflow-hidden">
                        {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="aspect-[4/3] w-[232px] shrink-0 rounded-2xl" />)}
                    </div>
                ) : projects.length === 0 ? (
                    <div className="rounded-2xl border border-dashed px-6 py-10 text-center text-sm text-muted-foreground">
                        {t('STUDIO.HOME.NONE', 'Nothing yet — your first production starts above.')}
                    </div>
                ) : showAll ? (
                    <div className="grid grid-cols-2 gap-3 animate-in fade-in sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                        {projects.map((p) => card(p))}
                    </div>
                ) : (
                    <HorizontalScroller previousLabel={t('STUDIO.HOME.PREVIOUS', 'Previous')} nextLabel={t('STUDIO.HOME.NEXT', 'Next')}>
                        {projects.slice(0, ROW_LIMIT).map((p) => card(p, 'w-[200px] shrink-0 snap-start sm:w-[232px]'))}
                        {projects.length > ROW_LIMIT && (
                            <button type="button" onClick={() => setShowAll(true)}
                                className="flex w-[160px] shrink-0 snap-start cursor-pointer flex-col items-center justify-center gap-1 rounded-2xl border border-dashed text-sm text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground">
                                <span className="text-lg font-medium text-foreground">+{projects.length - ROW_LIMIT}</span>
                                {t('STUDIO.HOME.VIEW_ALL', 'View all')}
                            </button>
                        )}
                    </HorizontalScroller>
                )}
            </section>

            <ConfirmDeleteDialog
                open={confirmBulkDelete}
                onOpenChange={setConfirmBulkDelete}
                title={picked.size === 1
                    ? t('STUDIO.HOME.DELETE_ONE_TITLE', 'Delete 1 production?')
                    : t('STUDIO.HOME.DELETE_MANY_TITLE', 'Delete {{n}} productions?', { n: picked.size })}
                description={t('STUDIO.HOME.DELETE_MANY_BODY', 'Their shots, photos and videos are deleted with them. This can’t be undone.')}
                confirmLabel={t('STUDIO.HOME.DELETE', 'Delete')}
                onConfirm={deletePicked}
            />

            <ProductPickerPanel
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onDrop={(items: any[]) => {
                    const infos: ProductInfo[] = items.map(productInfoFrom);
                    setProducts((ps) => [...ps, ...infos.filter((p) => !ps.some((x) => x.uuid === p.uuid))]);
                    setPickerOpen(false);
                }}
            />
        </PageContainer>
    );
};
